/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback} from 'react'
import {Header} from '@tanstack/react-table'
import {useTranslation} from 'react-i18next'
import UiDateRangePicker from 'components/Ui/UiDateRangePicker'
import UiTableTimeRangeFilter from 'components/Ui/UiTableTimeRangeFilter'
import UiNumberRangeFilter from 'components/Ui/UiNumberRangeFilter'
import UiPriceRangeFilter from 'components/Ui/UiPriceRangeFilter'
import UiTableColumnSearch from 'components/Ui/UiTableColumnSearch'
import UiTableColumnSelectFilter from 'components/Ui/UiTableColumnSelectFilter'
import {getTableRangeFilterConfig} from 'constants/TableRangeFilterConfig'
import {DateRange} from 'types/entities/DateRange'
import {OfferListRow} from './LeadOffersListTypes'

interface LeadOffersListHeaderFilterProps {
    header: Header<OfferListRow, unknown>
    statusOptions: { value: string; label: string }[]
    accountTypeOptions: { value: string; label: string }[]
    onResetPage: () => void
}

const LeadOffersListHeaderFilter: React.FC<LeadOffersListHeaderFilterProps> = ({
    header,
    statusOptions,
    accountTypeOptions,
    onResetPage,
}) => {
    const { t } = useTranslation()

    const handleChange = useCallback(
        (value: unknown) => {
            onResetPage()
            header.column.setFilterValue(value)
        },
        [header.column, onResetPage]
    )

    if (!header.column.getCanFilter()) return null

    if (header.id === 'created_at' || header.id === 'updated_at' || header.id === 'date') {
        return (
            <div className="max-width-date-range">
                <UiDateRangePicker
                    selectedDay={(header.column.getFilterValue() as DateRange) ?? null}
                    onChange={handleChange}
                    className="w-full"
                />
            </div>
        )
    }

    if (header.id === 'event_time') {
        return (
            <div className="w-full">
                <UiTableTimeRangeFilter
                    value={(header.column.getFilterValue() as { from?: string; to?: string }) ?? {}}
                    onChange={handleChange}
                />
            </div>
        )
    }

    if (header.id === 'persons' || header.id === 'calculations_count') {
        const config = getTableRangeFilterConfig(header.id)
        return (
            <div className={config?.containerClassName ?? 'min-w-[180px]'}>
                <UiNumberRangeFilter
                    value={(header.column.getFilterValue() as { from?: number; to?: number }) ?? {}}
                    onChange={handleChange}
                    placeholder={t('from - to')}
                    maxValue={config?.maxValue}
                    maxDigits={config?.maxDigits}
                />
            </div>
        )
    }

    if (header.id === 'value') {
        const config = getTableRangeFilterConfig(header.id)
        return (
            <div className={config?.containerClassName ?? 'min-w-[180px]'}>
                <UiPriceRangeFilter
                    value={(header.column.getFilterValue() as { from?: number; to?: number }) ?? {}}
                    onChange={(value) => handleChange(value)}
                    maxValue={config?.maxValue}
                    maxDigits={config?.maxDigits}
                />
            </div>
        )
    }

    if (header.id === 'status') {
        return (
            <div className="min-w-[180px]">
                <UiTableColumnSelectFilter
                    value={(header.column.getFilterValue() as string[]) ?? []}
                    onChange={handleChange}
                    options={statusOptions}
                    placeholder={t('All statuses')}
                />
            </div>
        )
    }

    if (header.id === 'company_type') {
        return (
            <div className="min-w-[180px]">
                <UiTableColumnSelectFilter
                    value={(header.column.getFilterValue() as string[]) ?? []}
                    onChange={handleChange}
                    options={accountTypeOptions}
                    placeholder={t('All types')}
                />
            </div>
        )
    }

    return (
        <div className="LeadOffersListHeaderFilter min-w-[130px]">
            <UiTableColumnSearch
                value={(header.column.getFilterValue() as string) ?? ''}
                onChange={handleChange}
                placeholder={t('Search')}
            />
        </div>
    )
}

export default LeadOffersListHeaderFilter
